import { Box, Paper } from '@mui/material';
import React from 'react';
import type AIChatMessage from '../models/AIChatMessage';
import { Palette } from 'Palette';
import { CHAT_WINDOW } from 'DesignTokens';
import { MarkdownRenderer } from './MarkdownRenderer';
import { StyledTypography } from './styles/styles';

const CHAT_COLORS = {
  userBackground: CHAT_WINDOW.useBrandedColors ? Palette.default.activeBg : '#1976d2',
  userText: CHAT_WINDOW.useBrandedColors ? Palette.tone2.activeText : '#fff',
  modelBackground: CHAT_WINDOW.useBrandedColors ? Palette.tone1.background : '#f5f5f5',
  modelText: CHAT_WINDOW.useBrandedColors ? Palette.default.text : 'rgba(0, 0, 0, 0.87)',
  modelBorder: CHAT_WINDOW.useBrandedColors ? Palette.tone1.border : 'rgba(0, 0, 0, 0.12)',
};

function ChatTimestamp({ message }: { message: AIChatMessage }) {
  if (!CHAT_WINDOW.showTimestamps) return null;
  return (
    <StyledTypography
      sx={{
        display: 'block',
        textAlign: message.role === 'user' ? 'right' : 'left',
        fontSize: CHAT_WINDOW.timestampFontSize,
        opacity: 0.65,
        mt: 0.5,
      }}
    >
      {message.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </StyledTypography>
  );
}

export default function ChatMessageBubble({
  message,
  renderModelContent,
}: {
  message: AIChatMessage;
  renderModelContent?: (message: AIChatMessage) => React.ReactNode;
}) {
  const isUser = message.role === 'user';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        mb: CHAT_WINDOW.messageGap,
        justifyContent: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      {CHAT_WINDOW.showAssistantAvatar && !isUser && (
        <Box
          component="img"
          src={CHAT_WINDOW.avatarImageSrc}
          alt=""
          sx={{
            flexShrink: 0,
            width: CHAT_WINDOW.avatarSize,
            height: CHAT_WINDOW.avatarSize,
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
      )}
      <Paper
        elevation={CHAT_WINDOW.bubbleElevation}
        sx={{
          py: CHAT_WINDOW.bubblePaddingY,
          px: CHAT_WINDOW.bubblePaddingX,
          fontSize: CHAT_WINDOW.bubbleFontSize,
          lineHeight: CHAT_WINDOW.bubbleLineHeight,
          maxWidth: '85%',
          bgcolor: isUser ? CHAT_COLORS.userBackground : CHAT_COLORS.modelBackground,
          color: isUser ? CHAT_COLORS.userText : CHAT_COLORS.modelText,
          border: isUser
            ? 'none'
            : `${CHAT_WINDOW.modelBubbleBorderWidth} solid ${CHAT_COLORS.modelBorder}`,
          borderRadius: isUser ? CHAT_WINDOW.userBubbleRadius : CHAT_WINDOW.modelBubbleRadius,
          wordWrap: 'break-word',
          ...(CHAT_WINDOW.tightenMarkdownSpacing
            ? { '& p': { marginTop: '3px', marginBottom: '3px' } }
            : {}),
        }}
      >
        {!isUser && renderModelContent ? (
          renderModelContent(message)
        ) : (
          <MarkdownRenderer>{message.parts.map((part) => part.text).join('\n\n')}</MarkdownRenderer>
        )}
        <ChatTimestamp message={message} />
      </Paper>
    </Box>
  );
}
